import { useRef, useState } from 'react';

export function useLocalStorage<T extends string | number | boolean | null>(
  key: string,
  initialValue: T
): [T, (newValue: T) => void] {
  return useStorage<T>(key, initialValue, 'local');
}
export function useSessionStorage<T extends string | number | boolean | null>(
  key: string,
  initialValue: T
): [T, (newValue: T) => void] {
  return useStorage<T>(key, initialValue, 'session');
}

function useStorage<T extends string | number | boolean | null>(
  key: string,
  initialValue: T,
  storageType: 'local' | 'session' = 'local'
): [T, (newValue: T) => void] {
  const store = useRef(createStore<T>(key, storageType));

  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      return store.current.get() ?? initialValue;
    } catch (error) {
      console.log(error);
      window.localStorage.removeItem(key);
      return initialValue;
    }
  });

  const setValue = (value: T) => {
    try {
      store.current.set(value);
      setStoredValue(value);
    } catch (error) {
      console.log(error);
    }
  };

  return [storedValue, setValue];
}

export const createStore = <T = unknown>(key: string, type: 'local' | 'session' = 'local') => ({
  get: () => JSON.parse(getStorage(type).getItem(key) ?? 'null') as T | null,
  set: (value: T) => getStorage(type).setItem(key, JSON.stringify(value)),
  remove: () => getStorage(type).removeItem(key),
});

const getStorage = (type: 'local' | 'session') => (type === 'local' ? localStorage : sessionStorage);
